import { ArrowRight, BotOff, CheckCircle2, Mic, Monitor, Sparkles } from "lucide-react";
import { useMetaTags } from "../components/MetaTags";
import { MeetingWorkflow } from "../components/MeetingWorkflow";

const BASE = "https://omnicanvasnotes.com";

const SAMPLE_MEETING_URL =
  "https://app.omnicanvasnotes.com/?mode=try&action=sample-meeting" +
  "&utm_source=marketing&utm_medium=meeting_notes_page&utm_campaign=no_bot_meeting_notes";
const RECORD_MEETING_URL =
  "https://app.omnicanvasnotes.com/?mode=try&action=record-meeting" +
  "&utm_source=marketing&utm_medium=meeting_notes_page&utm_campaign=no_bot_meeting_notes";

const outputs = [
  "An editable transcript that updates while you speak",
  "A concise recap of the conversation",
  "Decisions, action items, follow-ups, open questions, and dates",
  "Canvas blocks you can rearrange, connect, and link to other notes",
];

/** Compare capture models without implying that every competing product is bot-only. */
const comparison: { point: string; bots: string; omni: string }[] = [
  {
    point: "Who shows up in the participant list",
    bots: "A named bot every attendee can see, and often has to admit",
    omni: "Nobody — capture happens on your machine",
  },
  {
    point: "Transcription processing",
    bots: "Audio is sent to the provider for transcription",
    omni: "Choose on-device, cloud, or Auto; encrypted notes require on-device",
  },
  {
    point: "External and client calls",
    bots: "Guests may refuse the bot, or IT may block it",
    omni: "Nothing to admit, so nothing to refuse",
  },
  {
    point: "What you get afterwards",
    bots: "Typically a transcript and summary in a meeting library",
    omni: "Notes on the same canvas as the project they belong to",
  },
];

const faqs: { q: string; a: string }[] = [
  {
    q: "How do AI meeting notes work without a bot joining the call?",
    a: "OmniCanvas captures audio directly on your computer — your microphone, the system audio from a meeting tab, or both — instead of dialling a participant into the call. Because the capture is local, there is no extra attendee for anyone to admit, and the meeting tool you already use doesn't need to change.",
  },
  {
    q: "Does my meeting audio get uploaded?",
    a: "Transcription and recording sync are separate. On-device mode does not send audio to a transcription service; Cloud mode does, and Auto can use cloud processing when a local model is unavailable or fails. If a signed-in recording syncs, its audio can be uploaded for cross-device storage. For encrypted notes, the browser encrypts the recording before upload, transcription is forced to run on-device, and there is no cloud fallback.",
  },
  {
    q: "Is it legal to record a meeting this way?",
    a: "Recording laws vary by jurisdiction, and several US states plus many countries require every party to consent. OmniCanvas shows a consent reminder before recording, but the obligation is yours: tell participants you are recording, and get agreement before you start.",
  },
  {
    q: "How is this different from other AI meeting notetakers?",
    a: "Meeting tools use different capture models: some join as a visible bot, while others offer desktop capture. OmniCanvas does not require a bot, lets you choose on-device or cloud transcription, forces encrypted notes to use on-device transcription, and puts the output on an infinite canvas next to your project notes.",
  },
  {
    q: "Which meeting tools does it work with?",
    a: "OmniCanvas is not tied to a meeting-platform integration. It can record your microphone on supported devices, and supported desktop browsers can capture audio from a shared browser tab or screen. That covers browser-based calls in tools such as Zoom, Google Meet, Microsoft Teams, Slack, and Webex when the browser exposes their audio; exact system-audio support varies by browser, operating system, and what you share.",
  },
  {
    q: "Can I try it without recording a real meeting?",
    a: "Yes. The interactive sample walks a pre-recorded conversation through transcript, decisions and action items without an account. There is also a free browser tool that turns a transcript you already have into structured notes.",
  },
  {
    q: "What does it produce besides a transcript?",
    a: "A short recap, the decisions that were settled, action items with owners, open questions and dates — each as an editable canvas block you can move, link and connect to existing notes.",
  },
];

function jsonLd(): string {
  const url = `${BASE}/meeting-notes`;
  return JSON.stringify({
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "SoftwareApplication",
        name: "OmniCanvas Meeting Notes",
        description:
          "Capture meetings in your browser without adding a bot, choose on-device or cloud transcription, and turn the result into decisions and action items on an infinite canvas.",
        applicationCategory: "BusinessApplication",
        operatingSystem: "Any (web browser)",
        url,
        offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
        publisher: { "@type": "Organization", name: "OmniCanvas" },
      },
      {
        "@type": "FAQPage",
        mainEntity: faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: BASE },
          { "@type": "ListItem", position: 2, name: "Meeting Notes", item: url },
        ],
      },
    ],
  });
}

export function MeetingNotes() {
  useMetaTags({
    title: "AI Meeting Notes With No Bot Joining Your Call — OmniCanvas",
    description:
      "An AI note taker that captures meetings in your browser with no bot in the participant list. Choose on-device or cloud transcription.",
    url: `${BASE}/meeting-notes`,
  });

  return (
    <main className="pt-28 pb-24">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: jsonLd() }} />

      <section className="mx-auto max-w-5xl px-6 text-center">
        <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-indigo-100 bg-indigo-50 px-4 py-1.5 text-sm font-semibold text-indigo-700">
          <BotOff size={15} />
          No meeting bot in the participant list
        </div>
        <h1 className="mx-auto max-w-4xl text-4xl font-extrabold tracking-tight text-slate-900 sm:text-6xl">
          An AI note taker with no bot in your meeting
        </h1>
        <p className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-slate-600">
          Capture your microphone, a browser tab&apos;s system audio, or both.
          OmniCanvas transcribes the conversation and turns it into editable
          decisions, action items, and follow-ups on the same infinite canvas
          as the rest of your notes.
        </p>
        <div className="mt-9 flex flex-col items-center gap-3">
          <div className="flex flex-col justify-center gap-3 sm:flex-row">
            <a
              href={SAMPLE_MEETING_URL}
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-indigo-600 px-7 py-3.5 font-semibold text-white shadow-lg shadow-indigo-200 transition-colors hover:bg-indigo-700"
            >
              Try the transcript-to-actions sample
              <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
            </a>
            <a
              href={RECORD_MEETING_URL}
              className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-300 bg-white px-7 py-3.5 font-semibold text-slate-700 transition-colors hover:border-indigo-300 hover:text-indigo-700"
            >
              <Mic size={16} />
              Record a real meeting
            </a>
          </div>
          <p className="text-sm text-slate-500">
            The interactive sample needs no account. Live recording asks for a free account so transcription can run securely.
          </p>
        </div>
      </section>

      <div className="mt-12">
        <MeetingWorkflow source="meeting-notes" />
      </div>

      <section className="mx-auto mt-24 grid max-w-5xl gap-6 px-6 md:grid-cols-3">
        <div className="rounded-2xl border border-slate-200 p-7">
          <Mic className="mb-5 text-indigo-600" size={28} />
          <h2 className="text-xl font-bold text-slate-900">Choose the source</h2>
          <p className="mt-2 leading-relaxed text-slate-600">
            Record your microphone, system audio from a shared browser tab, or
            both together. Tell participants before recording; consent rules
            vary by location.
          </p>
        </div>
        <div className="rounded-2xl border border-slate-200 p-7">
          <Monitor className="mb-5 text-indigo-600" size={28} />
          <h2 className="text-xl font-bold text-slate-900">Keep the call bot-free</h2>
          <p className="mt-2 leading-relaxed text-slate-600">
            There is no extra attendee to admit. Capture happens in
            your browser without a meeting-platform integration. System and tab
            audio support varies by browser and device.
          </p>
        </div>
        <div className="rounded-2xl border border-slate-200 p-7">
          <Sparkles className="mb-5 text-indigo-600" size={28} />
          <h2 className="text-xl font-bold text-slate-900">Work from the result</h2>
          <p className="mt-2 leading-relaxed text-slate-600">
            Review the transcript, move the useful outputs around, connect
            them to project notes, and keep the meeting inside its real context.
          </p>
        </div>
      </section>

      <section className="mx-auto mt-24 max-w-4xl px-6">
        <div className="rounded-3xl bg-slate-900 p-8 text-white sm:p-12">
          <p className="text-sm font-semibold uppercase tracking-wider text-indigo-300">
            One capture package
          </p>
          <h2 className="mt-3 text-3xl font-bold">
            The outputs people actually return to
          </h2>
          <div className="mt-8 grid gap-4 sm:grid-cols-2">
            {outputs.map((output) => (
              <div key={output} className="flex items-start gap-3 rounded-xl bg-white/5 p-4">
                <CheckCircle2 className="mt-0.5 shrink-0 text-indigo-300" size={19} />
                <span className="text-slate-200">{output}</span>
              </div>
            ))}
          </div>
          <a
            href={SAMPLE_MEETING_URL}
            className="mt-9 inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 font-semibold text-indigo-700 transition-colors hover:bg-indigo-50"
          >
            Try the interactive sample
            <ArrowRight size={16} />
          </a>
        </div>
      </section>

      {/* Compare the optional bot-based workflow with browser capture. */}
      <section className="mx-auto mt-24 max-w-4xl px-6">
        <h2 className="text-3xl font-bold tracking-tight text-slate-900">
          Bot-based capture vs. browser capture
        </h2>
        <p className="mt-3 leading-relaxed text-slate-600">
          AI meeting tools use different capture models: some join as a visible
          participant, while others record from a desktop app. OmniCanvas captures
          through your browser without adding a participant and lets you choose how
          transcription is processed. For sensitive calls, encrypted notes require
          on-device transcription and never fall back to cloud processing.
        </p>
        <div className="mt-8 overflow-x-auto">
          <table className="w-full min-w-[36rem] border-collapse text-left text-sm">
            <thead>
              <tr className="border-b border-slate-200">
                <th className="py-3 pr-4 font-semibold text-slate-500"></th>
                <th className="py-3 pr-4 font-semibold text-slate-700">
                  Bot-based workflow
                </th>
                <th className="py-3 font-semibold text-indigo-700">OmniCanvas</th>
              </tr>
            </thead>
            <tbody>
              {comparison.map((row) => (
                <tr key={row.point} className="border-b border-slate-100 align-top">
                  <th scope="row" className="py-4 pr-4 font-medium text-slate-900">
                    {row.point}
                  </th>
                  <td className="py-4 pr-4 text-slate-600">{row.bots}</td>
                  <td className="py-4 text-slate-700">{row.omni}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-6 text-sm text-slate-500">
          Weighing up the alternatives? We keep a running comparison of{" "}
          <a
            href="/blog/reviews/best-ai-meeting-note-takers"
            className="font-medium text-indigo-600 underline"
          >
            the best AI meeting note takers
          </a>
          , plus dedicated breakdowns of{" "}
          <a href="/blog/reviews/otter-ai-alternatives" className="font-medium text-indigo-600 underline">
            Otter.ai
          </a>
          ,{" "}
          <a href="/blog/reviews/granola-alternatives" className="font-medium text-indigo-600 underline">
            Granola
          </a>{" "}
          and{" "}
          <a href="/blog/reviews/plaud-alternatives" className="font-medium text-indigo-600 underline">
            Plaud
          </a>
          .
        </p>
      </section>

      {/* Free-tool bridge: the strongest entry point for visitors who aren't
          ready to install anything but already have a transcript. */}
      <section className="mx-auto mt-24 max-w-4xl px-6">
        <div className="rounded-3xl border border-indigo-100 bg-indigo-50/60 p-8 sm:p-10">
          <h2 className="text-2xl font-bold text-slate-900">
            Already have a transcript? Turn it into notes free
          </h2>
          <p className="mt-3 leading-relaxed text-slate-600">
            If your meeting tool already exported a transcript, you don&apos;t need
            an account to get something useful out of it. Paste it into the free
            AI meeting notes generator and get a summary, the decisions, action
            items and open questions — extracted in your browser, with nothing
            uploaded.
          </p>
          <a
            href="/tools/ai-meeting-notes"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-indigo-600 px-6 py-3 font-semibold text-white transition-colors hover:bg-indigo-700"
          >
            Open the free meeting notes generator
            <ArrowRight size={16} />
          </a>
        </div>
      </section>

      <section className="mx-auto mt-24 max-w-3xl px-6">
        <h2 className="text-3xl font-bold tracking-tight text-slate-900">
          Frequently asked questions
        </h2>
        <dl className="mt-8 space-y-8">
          {faqs.map((faq) => (
            <div key={faq.q}>
              <dt className="text-lg font-semibold text-slate-900">{faq.q}</dt>
              <dd className="mt-2 leading-relaxed text-slate-600">{faq.a}</dd>
            </div>
          ))}
        </dl>
      </section>
    </main>
  );
}
